import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Slider } from '../ui/slider';
import { CheckCircle2, XCircle, MessageSquare, Lock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal de validation de devis pour le client
 * Permet d'accepter, refuser ou négocier le devis
 * PM Spec 10/05/2026 — anti-abus négociation : bouton "Négocier" masqué si désactivé.
 */

const QuoteValidationModal = ({ isOpen, onClose, operation, onSubmit }) => {
  const { toast } = useToast();
  const [action, setAction] = useState(null); // 'accept', 'refuse', 'negotiate'

  // Utiliser le bon champ de prix (montantTotalTTC > estimatedPrice > finalPrice)
  const quotePrice = operation?.montantTotalTTC || operation?.estimatedPrice || operation?.finalPrice || 0;

  const [negotiationPrice, setNegotiationPrice] = useState(quotePrice);
  const [negotiationMessage, setNegotiationMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // PM Spec — interrogation backend pour savoir si négociation autorisée
  // (combine flag op-level + config global allow_client_initiate)
  const [canNegotiate, setCanNegotiate] = useState(null); // null=loading, true/false résolu

  useEffect(() => {
    if (!isOpen || !operation?.id) return;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/quote/${operation.id}/can-negotiate`, {
        });
        if (!cancelled) setCanNegotiate(Boolean(r.data?.allowed));
      } catch {
        // En cas d'erreur réseau, on retombe sur le flag local pour ne pas bloquer.
        if (!cancelled) setCanNegotiate(operation?.negotiationEnabled === true);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [isOpen, operation?.id, operation?.negotiationEnabled]);

  const handleSubmit = async () => {
    if (!action) {
      toast({
        title: 'Action requise',
        description: 'Veuillez choisir une action',
        variant: 'destructive'
      });
      return;
    }

    if (action === 'negotiate' && !negotiationMessage.trim()) {
      toast({
        title: 'Message requis',
        description: 'Veuillez ajouter un message pour la négociation',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        action,
        proposedPrice: action === 'negotiate' ? negotiationPrice : null,
        message: action === 'negotiate' ? negotiationMessage : null
      });
      
      onClose();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message || 'Une erreur est survenue',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Validation du Devis</DialogTitle>
          <DialogDescription>
            Opération #{operation?.operationNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Montant du devis */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6">
            <p className="text-sm text-gray-600 mb-1">Montant proposé</p>
            <p className="text-3xl font-bold text-emerald-700">
              {formatPrice(quotePrice)}
            </p>
            {operation?.montantTotalHT && (
              <div className="mt-3 text-sm text-gray-600 space-y-1">
                <p>HT: {formatPrice(operation.montantTotalHT)}</p>
                {operation.montantTVA && <p>TVA (18%): {formatPrice(operation.montantTVA)}</p>}
                {operation.insuranceAmount && <p>Assurance: {formatPrice(operation.insuranceAmount)}</p>}
              </div>
            )}
          </div>

          {/* Choix de l'action */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Votre décision</Label>

            <div className={`grid grid-cols-1 gap-3 ${canNegotiate ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
              {/* Accepter */}
              <button
                type="button"
                data-testid="quote-action-accept"
                onClick={() => setAction('accept')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  action === 'accept'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-emerald-300 bg-white'
                }`}
              >
                <CheckCircle2 className={`w-8 h-8 mx-auto mb-2 ${
                  action === 'accept' ? 'text-emerald-600' : 'text-gray-400'
                }`} />
                <p className={`font-semibold ${
                  action === 'accept' ? 'text-emerald-700' : 'text-gray-700'
                }`}>
                  Accepter
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Valider le devis tel quel
                </p>
              </button>

              {/* Refuser */}
              <button
                type="button"
                data-testid="quote-action-refuse"
                onClick={() => setAction('refuse')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  action === 'refuse'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-red-300 bg-white'
                }`}
              >
                <XCircle className={`w-8 h-8 mx-auto mb-2 ${
                  action === 'refuse' ? 'text-red-600' : 'text-gray-400'
                }`} />
                <p className={`font-semibold ${
                  action === 'refuse' ? 'text-red-700' : 'text-gray-700'
                }`}>
                  Refuser
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Annuler cette opération
                </p>
              </button>

              {/* Négocier — masqué si désactivé (PM Spec 10/05/2026 anti-abus) */}
              {canNegotiate && (
                <button
                  type="button"
                  data-testid="quote-action-negotiate"
                  onClick={() => setAction('negotiate')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    action === 'negotiate'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300 bg-white'
                  }`}
                >
                  <MessageSquare className={`w-8 h-8 mx-auto mb-2 ${
                    action === 'negotiate' ? 'text-orange-600' : 'text-gray-400'
                  }`} />
                  <p className={`font-semibold ${
                    action === 'negotiate' ? 'text-orange-700' : 'text-gray-700'
                  }`}>
                    Négocier
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Proposer un autre tarif
                  </p>
                </button>
              )}
            </div>

            {/* Indicateur subtil quand la négociation est désactivée (rassure mais ne crée pas d'attente) */}
            {canNegotiate === false && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-zinc-50 border border-zinc-200" data-testid="negotiation-disabled-notice">
                <Lock className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
                <p className="text-xs text-zinc-600">
                  Le tarif proposé est ferme. Pour toute question, contactez votre commercial Baobab.
                </p>
              </div>
            )}
          </div>

          {/* Section Négociation */}
          {action === 'negotiate' && (
            <div className="space-y-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="space-y-2">
                <Label htmlFor="negotiation-price">
                  Votre proposition ({formatPrice(negotiationPrice)})
                </Label>
                <Slider
                  id="negotiation-price"
                  min={Math.floor(quotePrice * 0.5)}
                  max={Math.ceil(quotePrice * 1.5)}
                  step={10000}
                  value={[negotiationPrice]}
                  onValueChange={(value) => setNegotiationPrice(value[0])}
                  className="py-4"
                />
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{formatPrice(Math.floor(quotePrice * 0.5))}</span>
                  <span>{formatPrice(Math.ceil(quotePrice * 1.5))}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="negotiation-message">
                  Message / Justification *
                </Label>
                <Textarea
                  id="negotiation-message"
                  placeholder="Expliquez pourquoi vous proposez ce montant..."
                  value={negotiationMessage}
                  onChange={(e) => setNegotiationMessage(e.target.value)}
                  rows={4}
                  className="resize-none"
                />
              </div>
            </div>
          )}

          {/* Message de confirmation */}
          {action === 'accept' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-sm text-emerald-700">
                ✅ En acceptant ce devis, l'opération passera en phase de planification.
              </p>
            </div>
          )}

          {action === 'refuse' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">
                ⚠️ En refusant ce devis, l'opération sera annulée. Cette action est irréversible.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!action || isSubmitting}
            className={action === 'accept' ? 'bg-emerald-600 hover:bg-emerald-700' : 
                      action === 'refuse' ? 'bg-red-600 hover:bg-red-700' :
                      action === 'negotiate' ? 'bg-orange-600 hover:bg-orange-700' : ''}
          >
            {isSubmitting ? 'Envoi...' : 
             action === 'accept' ? 'Accepter le devis' :
             action === 'refuse' ? 'Confirmer le refus' :
             action === 'negotiate' ? 'Envoyer la proposition' :
             'Confirmer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteValidationModal;